import React, { useState, useEffect } from 'react';
import '../styles/OneOnOneCalendar.css';

function OneOnOneCalendar({ meetings, onMeetingClick, onDateClick }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarDays, setCalendarDays] = useState([]);

    useEffect(() => {
        generateCalendar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate, meetings]);

    const generateCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // Get first day of month and total days
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

        // Get days from previous month
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        const prevMonthDays = startingDayOfWeek;

        // Generate 42 days (6 weeks) for consistent layout
        const days = [];

        // Previous month days
        for (let i = prevMonthDays - 1; i >= 0; i--) {
            const day = prevMonthLastDay - i;
            const date = new Date(year, month - 1, day);
            days.push({
                date,
                day,
                isCurrentMonth: false,
                isToday: false,
                meetings: []
            });
        }

        // Current month days
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateStr = date.toDateString();
            const todayStr = today.toDateString();

            // Filter meetings for this day
            const dayMeetings = meetings.filter(meeting => {
                const meetingDate = new Date(meeting.scheduledDate);
                return meetingDate.toDateString() === dateStr;
            });

            days.push({
                date,
                day,
                isCurrentMonth: true,
                isToday: dateStr === todayStr,
                meetings: dayMeetings
            });
        }

        // Next month days to fill the grid
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const date = new Date(year, month + 1, day);
            days.push({
                date,
                day,
                isCurrentMonth: false,
                isToday: false,
                meetings: []
            });
        }

        setCalendarDays(days);
    };

    const goToPreviousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const getMonthYearString = () => {
        return currentDate.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/Los_Angeles' // US Pacific Time (PST/PDT)
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'scheduled': '#3B82F6',
            'completed': '#10B981',
            'cancelled': '#EF4444',
            'in-progress': '#F59E0B'
        };
        return colors[status] || '#6C757D';
    };

    const handleDayClick = (dayData) => {
        if (dayData.meetings.length > 0) {
            // If there are meetings, show the first one
            onMeetingClick(dayData.meetings[0]);
        } else if (dayData.isCurrentMonth && onDateClick) {
            // If it's an empty day in current month, allow creating new meeting
            onDateClick(dayData.date);
        }
    };

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="one-on-one-calendar">
            {/* Calendar Header */}
            <div className="calendar-header">
                <div className="calendar-nav">
                    <button className="nav-button" onClick={goToPreviousMonth} title="Previous month">
                        ‹
                    </button>
                    <h2 className="month-year">{getMonthYearString()}</h2>
                    <button className="nav-button" onClick={goToNextMonth} title="Next month">
                        ›
                    </button>
                </div>
                <button className="today-button" onClick={goToToday}>
                    Today
                </button>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
                {/* Week day headers */}
                {weekDays.map(day => (
                    <div key={day} className="calendar-weekday">
                        {day}
                    </div>
                ))}

                {/* Calendar days */}
                {calendarDays.map((dayData, index) => (
                    <div
                        key={index}
                        className={`calendar-day ${!dayData.isCurrentMonth ? 'other-month' : ''} ${dayData.isToday ? 'today' : ''
                            } ${dayData.meetings.length > 0 ? 'has-meetings' : ''}`}
                        onClick={() => handleDayClick(dayData)}
                    >
                        <div className="day-number">{dayData.day}</div>

                        {/* Meeting events */}
                        <div className="day-meetings">
                            {dayData.meetings.slice(0, 3).map((meeting, idx) => (
                                <div
                                    key={meeting.id || idx}
                                    className="meeting-event"
                                    style={{ borderLeftColor: getStatusColor(meeting.status) }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMeetingClick(meeting);
                                    }}
                                    title={`${formatTime(meeting.scheduledDate)} - ${meeting.participantName}`}
                                >
                                    <span className="meeting-time">{formatTime(meeting.scheduledDate)}</span>
                                    <span className="meeting-participant">{meeting.participantName}</span>
                                </div>
                            ))}

                            {/* Show "+X more" if there are more than 3 meetings */}
                            {dayData.meetings.length > 3 && (
                                <div className="more-meetings">
                                    +{dayData.meetings.length - 3} more
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Legend */}
            <div className="calendar-legend">
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: '#3B82F6' }}></span>
                    <span>Scheduled</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: '#10B981' }}></span>
                    <span>Completed</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: '#EF4444' }}></span>
                    <span>Cancelled</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: '#F59E0B' }}></span>
                    <span>In Progress</span>
                </div>
            </div>
        </div>
    );
}

export default OneOnOneCalendar;
